import { IUser } from "../api/User/User.types";

const AUTH_API_BASE =
  process.env.REACT_APP_AUTH_BACKEND_SERVICE_URL || process.env.REACT_APP_AUTH_BACKEND_SERVICE_URL || "";
const USER_DETAILS_PATH = "/api/user_details";

/**
 * Decode JWT payload without verification (client-side only).
 * Handles standard 3-part JWT and base64url. Returns the payload object or null if invalid.
 */
export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  if (!token || typeof token !== "string") return null;
  const t = token.trim();
  try {
    const parts = t.split(".");
    let payloadSegment: string;
    if (parts.length >= 2) {
      payloadSegment = parts[1];
    } else if (parts.length === 1) {
      payloadSegment = t;
    } else {
      return null;
    }
    const base64 = payloadSegment.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = atob(base64);
    let json: string;
    try {
      json = decodeURIComponent(
        decoded
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
    } catch {
      json = decoded;
    }
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Try to parse a single base64 string (no dots) as JSON payload.
 */
function decodeSinglePartPayload(token: string): Record<string, unknown> | null {
  if (!token?.trim()) return null;
  try {
    const base64 = token.trim().replace(/-/g, "+").replace(/_/g, "/");
    const decoded = atob(base64);
    let json: string;
    try {
      json = decodeURIComponent(
        decoded
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
    } catch {
      json = decoded;
    }
    const out = JSON.parse(json);
    return out && typeof out === "object" ? out : null;
  } catch {
    return null;
  }
}

/** Normalize parsed user data to IUser. */
function parseRawUserJsonToIUser(raw: Record<string, unknown>): IUser | null {
  if (!raw || typeof raw !== "object") return null;
  try {
    const user: IUser = {
      userId: String(raw.userID ?? raw.userId ?? ""),
      accountType: (raw.accountType as IUser["accountType"]) ?? 3,
      sessionID: String(raw.sessionID ?? ""),
      permissions: Array.isArray(raw.permissions) ? raw.permissions : [],
      name: String(raw.name ?? ""),
      email: String(raw.email ?? ""),
      username: String(raw.username ?? ""),
    };
    if (raw.defaultConsignee != null)
      user.defaultConsignee = raw.defaultConsignee as IUser["defaultConsignee"];
    if (raw.shippers != null) user.shippers = raw.shippers as IUser["shippers"];
    if (raw.consignees != null)
      user.consignees = raw.consignees as IUser["consignees"];
    if (raw.portalNotice != null)
      user.portalNotice = String(raw.portalNotice);
    if (raw.image != null) user.image = String(raw.image);
    return user;
  } catch {
    return null;
  }
}

function extractUserFromPayload(inner: Record<string, unknown>): IUser | null {
  return parseRawUserJsonToIUser(inner);
}

/**
 * Extract IUser from user_details API response body.
 * Expected shape: { status, message, data: { id, username, entity_url_email, first_name, last_name, ... } }.
 * Builds user directly from the response data fields.
 */
function extractUserFromUserDetailsResponse(body: unknown): IUser | null {
  if (!body || typeof body !== "object") return null;
  const obj = body as Record<string, unknown>;
  const data = obj.data as Record<string, unknown> | undefined;
  const target = data && typeof data === "object" ? data : obj;

  const firstName = target.first_name != null ? String(target.first_name) : "";
  const lastName = target.last_name != null ? String(target.last_name) : "";
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();

  const user = parseRawUserJsonToIUser({
    userID: target.id,
    username: target.username ?? target.entity_url_email ?? "",
    email: target.entity_url_email ?? target.username ?? "",
    name: fullName || String(target.username ?? ""),
    permissions: [],
    accountType: 3,
    sessionID: "",
  });

  return user;
}

/**
 * Fetch user details from the auth API using the access token.
 * Uses Authorization header with the raw token (no "Bearer " prefix).
 * Returns IUser or null on failure.
 */
export async function fetchUserDetails(accessToken: string): Promise<IUser | null> {
  if (!accessToken?.trim() || !AUTH_API_BASE) return null;
  try {
    const url = `${AUTH_API_BASE.replace(/\/$/, "")}${USER_DETAILS_PATH}`;
    const res = await fetch(url, {
      method: "GET",
      headers: {
        Accept: "application/json",
        Authorization: accessToken.trim(),
      },
      credentials: "omit",
    });
    if (!res.ok) return null;
    const body = await res.json();
    return extractUserFromUserDetailsResponse(body);
  } catch {
    return null;
  }
}

/**
 * Extract IUser from access_token. Supports:
 * 1. Standard JWT: decode payload and extract user fields.
 * 2. Nested JWT: outer payload.message = inner JWT string.
 * 3. Single-part token: whole token is base64 JSON.
 */
export function getUserFromAccessToken(accessToken: string): IUser | null {
  if (!accessToken?.trim()) return null;

  const outer = decodeJwtPayload(accessToken);
  if (!outer) {
    const single = decodeSinglePartPayload(accessToken);
    if (!single) return null;
    const user = extractUserFromPayload(single);
    if (user) return user;
    const msg = single.message;
    if (typeof msg === "string") {
      const inner = decodeJwtPayload(msg) ?? decodeSinglePartPayload(msg);
      if (inner) return extractUserFromPayload(inner);
    }
    return null;
  }

  const user = extractUserFromPayload(outer);
  if (user) return user;

  const message = outer.message;
  const innerToken = typeof message === "string" ? message : null;
  if (!innerToken) return null;

  const inner = decodeJwtPayload(innerToken) ?? decodeSinglePartPayload(innerToken);
  if (!inner) return null;

  return extractUserFromPayload(inner);
}
